/**
 * MyAttendanceDialog.jsx - Dialog pentru vizualizarea pontajului personal al angajatului
 */

import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Clock, Coffee, Sun, Briefcase, TrendingUp, AlertTriangle, ChevronLeft, ChevronRight, PenTool, Check, FileSignature, Wallet, BarChart3, ChevronDown, ChevronUp } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import SignaturePad from "./SignaturePad";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper: format date as YYYY-MM-DD using LOCAL timezone (not UTC) - same as admin grid
const toLocalDateStr = (d) => {
  if (!d) return '';
  if (typeof d === 'string') {
    // For ISO strings like "2026-03-10T00:00:00+02:00", extract the date part directly
    // This avoids timezone conversion issues that can shift dates by 1 day
    const match = d.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
    d = new Date(d);
  }
  const date = (d instanceof Date) ? d : new Date(d);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Calculare Paște Ortodox (Meeus Julian algorithm — corect pentru calendar ortodox)
const calculateOrthodoxEaster = (year) => {
  const a = year % 4;
  const b = year % 7;
  const c = year % 19;
  const d = (19 * c + 15) % 30;
  const e = (2 * a + 4 * b - d + 34) % 7;
  const month = Math.floor((d + e + 114) / 31); // 3 = Martie, 4 = Aprilie
  const day = ((d + e + 114) % 31) + 1;
  const julianEaster = new Date(year, month - 1, day);
  // Conversie Iulian → Gregorian (13 zile pentru 1900-2099, 14 pentru 2100+)
  const offsetDays = year < 2100 ? 13 : 14;
  julianEaster.setDate(julianEaster.getDate() + offsetDays);
  return julianEaster;
};

// Sărbători legale România
const getRomanianHolidays = (year) => {
  const fixedHolidays = [
    `${year}-01-01`, `${year}-01-02`, `${year}-01-24`, `${year}-05-01`,
    `${year}-06-01`, `${year}-08-15`, `${year}-11-30`, `${year}-12-01`,
    `${year}-12-25`, `${year}-12-26`
  ];
  
  const easter = calculateOrthodoxEaster(year);
  const goodFriday = new Date(easter);
  goodFriday.setDate(goodFriday.getDate() - 2);
  const easterMonday = new Date(easter);
  easterMonday.setDate(easterMonday.getDate() + 1);
  const pentecost = new Date(easter);
  pentecost.setDate(pentecost.getDate() + 49);
  const pentecostMonday = new Date(pentecost);
  pentecostMonday.setDate(pentecostMonday.getDate() + 1);
  
  // Folosim data locală (NU toISOString — care convertește la UTC și poate decala cu o zi)
  const formatDate = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  
  return new Set([
    ...fixedHolidays,
    formatDate(goodFriday),
    formatDate(easter),
    formatDate(easterMonday),
    formatDate(pentecost),
    formatDate(pentecostMonday)
  ]);
};

const dayNames = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
const monthNames = [
  'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie',
  'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'
];

export default function MyAttendanceDialog({ open, onOpenChange, userId, userName, companyId, userSalary }) {
  const [loading, setLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState(null);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [signatureStatus, setSignatureStatus] = useState(null);
  const [showEarnings, setShowEarnings] = useState(false);
  
  const holidays = getRomanianHolidays(year);
  
  useEffect(() => {
    if (open && userId) {
      fetchAttendanceData();
      fetchSignatureStatus();
    }
  }, [open, userId, month, year]);
  
  const fetchAttendanceData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/monthly/${userId}/${year}/${month}`);
      setAttendanceData(response.data);
    } catch (error) {
      console.error("Error fetching attendance:", error);
      toast.error("Eroare la încărcarea datelor");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSignatureStatus = async () => {
    try {
      const response = await axios.get(`${API}/signatures/status/${userId}/${month}/${year}`);
      setSignatureStatus(response.data);
    } catch (error) {
      console.error("Error fetching signature status:", error);
    }
  };
  
  const handleSignatureComplete = () => {
    fetchSignatureStatus();
    toast.success("Semnătură înregistrată cu succes!");
  };
  
  // Check if this is a previous month (can be signed)
  const today = new Date();
  const isPreviousMonth = year < today.getFullYear() || 
    (year === today.getFullYear() && month < today.getMonth() + 1);
  const canSign = isPreviousMonth && (!signatureStatus || signatureStatus.can_sign);
  
  const getDayStatus = (day) => {
    if (!attendanceData) return { type: 'no-data', color: 'bg-slate-100', label: '-' };
    
    const date = new Date(year, month - 1, day);
    const dateStr = toLocalDateStr(date);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    const isHoliday = holidays.has(dateStr);
    
    // Check overtime FIRST (same as admin grid)
    const overtime = attendanceData.overtime?.find(o => toLocalDateStr(o.date) === dateStr);
    if (overtime) {
      return { type: 'overtime', color: 'bg-amber-500', label: `${overtime.hours}h`, hours: overtime.hours, equivalentHours: overtime.equivalent_hours || overtime.hours * 1.5 };
    }
    
    if (isWeekend) return { type: 'weekend', color: 'bg-slate-200', label: 'W' };
    if (isHoliday) return { type: 'holiday', color: 'bg-yellow-400', label: 'SL' };
    
    // Check leaves
    const leave = attendanceData.leaves?.find(l => {
      const start = toLocalDateStr(l.start_date);
      const end = toLocalDateStr(l.end_date);
      return dateStr >= start && dateStr <= end && l.status === 'approved';
    });
    if (leave) {
      const colors = { 'CO': 'bg-orange-400', 'CM': 'bg-red-400', 'CS': 'bg-purple-400', 'FS': 'bg-pink-400' };
      return { type: leave.type, color: colors[leave.type] || 'bg-orange-400', label: leave.type };
    }
    
    // Check absences - distinguish between Absent (A), Invoire (I), and Delegație (D)
    const absence = attendanceData.absences?.find(a => toLocalDateStr(a.date) === dateStr);
    if (absence) {
      if (absence.type === 'invoire') {
        return { type: 'invoire', color: 'bg-blue-400', label: 'I' };
      }
      if (absence.type === 'delegate') {
        return { type: 'delegate', color: 'bg-teal-500', label: 'D' };
      }
      return { type: 'absence', color: 'bg-gray-500', label: 'A' };
    }
    
    // Check attendance
    const attendance = attendanceData.attendances?.find(a => toLocalDateStr(a.check_in) === dateStr);
    if (attendance) {
      return { 
        type: 'present', 
        color: attendance.is_auto_generated ? 'bg-green-300' : 'bg-green-500', 
        label: `${attendance.hours_worked?.toFixed(1) || 8}h`,
        hours: attendance.hours_worked || 8
      };
    }
    
    return { type: 'no-data', color: 'bg-slate-100 border border-slate-200', label: '-' };
  };
  
  // Generate calendar days - MUST be defined BEFORE stats calculation that uses daysInMonth
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayOfWeek = new Date(year, month - 1, 1).getDay();
  
  // Calculate statistics from getDayStatus() to match the grid exactly
  const stats = (() => {
    let zileLucrate = 0, oreLucrate = 0, oreSuplimentare = 0;
    let zileCO = 0, zileCM = 0, zileAbsente = 0, zileInvoire = 0, zileDelegatie = 0;
    
    for (let day = 1; day <= daysInMonth; day++) {
      const s = getDayStatus(day);
      if (s.type === 'present') {
        zileLucrate++;
        oreLucrate += s.hours || 8;
      } else if (s.type === 'overtime') {
        oreSuplimentare += s.hours || 0;
      } else if (s.type === 'CO') {
        zileCO++;
      } else if (s.type === 'CM') {
        zileCM++;
      } else if (s.type === 'absence') {
        zileAbsente++;
      } else if (s.type === 'invoire') {
        zileInvoire++;
      } else if (s.type === 'delegate') {
        zileDelegatie++;
      }
    }
    
    return { zileLucrate, oreLucrate, oreSuplimentare, zileCO, zileCM, zileAbsente, zileInvoire, zileDelegatie };
  })();
  
  const prevMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };
  
  const nextMonth = () => {
    if (month === 12) {
      setMonth(1);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="my-attendance-dialog">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Calendar className="w-5 h-5 text-teal-600" />
            Pontajul Meu
          </DialogTitle>
          <DialogDescription>
            Vizualizează prezența și statisticile tale lunare
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          {/* Month Selector */}
          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={prevMonth}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <h3 className="text-lg font-bold text-slate-800">
              {monthNames[month - 1]} {year}
            </h3>
            <Button variant="outline" size="sm" onClick={nextMonth}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Statistics Cards */}
          <div className="grid grid-cols-3 gap-3">
            <Card className="bg-gradient-to-br from-green-50 to-teal-50 border-green-200">
              <CardContent className="p-3 text-center">
                <Briefcase className="w-5 h-5 text-green-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-green-700">{stats.zileLucrate}</p>
                <p className="text-xs text-green-600">Zile lucrate</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="p-3 text-center">
                <Clock className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-blue-700">{stats.oreLucrate.toFixed(1)}h</p>
                <p className="text-xs text-blue-600">Ore lucrate</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
              <CardContent className="p-3 text-center">
                <TrendingUp className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-amber-700">{stats.oreSuplimentare.toFixed(1)}h</p>
                <p className="text-xs text-amber-600">Ore suplimentare</p>
              </CardContent>
            </Card>
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            <Card className="bg-gradient-to-br from-orange-50 to-red-50 border-orange-200">
              <CardContent className="p-3 text-center">
                <Sun className="w-5 h-5 text-orange-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-orange-700">{stats.zileCO}</p>
                <p className="text-xs text-orange-600">Zile CO</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-red-50 to-pink-50 border-red-200">
              <CardContent className="p-3 text-center">
                <Coffee className="w-5 h-5 text-red-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-red-700">{stats.zileCM}</p>
                <p className="text-xs text-red-600">Zile CM</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-slate-50 to-gray-50 border-slate-200">
              <CardContent className="p-3 text-center">
                <AlertTriangle className="w-5 h-5 text-slate-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-slate-700">{stats.zileAbsente}</p>
                <p className="text-xs text-slate-600">Zile absente</p>
              </CardContent>
            </Card>
          </div>
          
          {/* Calendar Grid */}
          <Card>
            <CardContent className="p-3">
              {loading ? (
                <div className="text-center py-8 text-slate-500">Se încarcă...</div>
              ) : (
                <>
                  {/* Day names header */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {dayNames.map((name, idx) => (
                      <div 
                        key={`day-${name}`} 
                        className={`text-center text-xs font-semibold py-1 ${
                          idx === 0 || idx === 6 ? 'text-red-500' : 'text-slate-600'
                        }`}
                      >
                        {name}
                      </div>
                    ))}
                  </div>
                  
                  {/* Calendar grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {/* Empty cells for first week */}
                    {Array.from({ length: firstDayOfWeek }).map((_, idx) => (
                      <div key={`empty-${idx}`} className="aspect-square"></div>
                    ))}
                    
                    {/* Days */}
                    {Array.from({ length: daysInMonth }).map((_, idx) => {
                      const day = idx + 1;
                      const status = getDayStatus(day);
                      const date = new Date(year, month - 1, day);
                      const isToday = new Date().toDateString() === date.toDateString();
                      
                      return (
                        <div
                          key={day}
                          className={`aspect-square rounded-lg flex flex-col items-center justify-center text-xs relative
                            ${status.color} ${isToday ? 'ring-2 ring-teal-500 ring-offset-1' : ''}
                            ${status.type === 'present' || status.type === 'overtime' ? 'text-white' : ''}
                          `}
                          title={`${day} ${monthNames[month - 1]} - ${status.label}`}
                        >
                          <span className={`font-semibold ${isToday ? 'text-teal-600' : ''}`}>{day}</span>
                          <span className="text-[10px] font-medium">{status.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
          
          {/* Legend */}
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge className="bg-green-500">P - Prezent</Badge>
            <Badge className="bg-amber-500">OS - Ore Supl.</Badge>
            <Badge className="bg-yellow-400 text-yellow-900">SL - Sărbătoare</Badge>
            <Badge className="bg-slate-300 text-slate-700">W - Weekend</Badge>
            <Badge className="bg-orange-400">CO - Concediu</Badge>
            <Badge className="bg-red-400">CM - Medical</Badge>
            <Badge className="bg-blue-400">I - Învoire</Badge>
            <Badge className="bg-teal-500">D - Delegație</Badge>
            <Badge className="bg-gray-500">A - Absent</Badge>
          </div>
          
          {/* Signature Section for Previous Months */}
          {isPreviousMonth && (
            <Card className={`border-2 ${signatureStatus?.has_signature ? 'border-green-300 bg-green-50' : 'border-orange-300 bg-orange-50'}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      signatureStatus?.has_signature ? 'bg-green-500' : 'bg-orange-500'
                    }`}>
                      {signatureStatus?.has_signature ? (
                        <Check className="w-5 h-5 text-white" />
                      ) : (
                        <FileSignature className="w-5 h-5 text-white" />
                      )}
                    </div>
                    <div>
                      <p className={`font-semibold ${signatureStatus?.has_signature ? 'text-green-800' : 'text-orange-800'}`}>
                        {signatureStatus?.has_signature 
                          ? `Pontaj semnat (${signatureStatus.signature_count}/2)` 
                          : 'Pontaj nesemnat'}
                      </p>
                      <p className="text-xs text-slate-600">
                        {signatureStatus?.has_signature 
                          ? `Ultima semnătură: ${new Date(signatureStatus.last_signed_at).toLocaleDateString('ro-RO')}`
                          : 'Semnează pentru a confirma datele din pontaj'}
                      </p>
                    </div>
                  </div>
                  
                  {canSign && (
                    <Button
                      onClick={() => setShowSignaturePad(true)}
                      className={`${
                        signatureStatus?.has_signature 
                          ? 'bg-amber-600 hover:bg-amber-700' 
                          : 'bg-teal-600 hover:bg-teal-700'
                      }`}
                      data-testid="open-signature-button"
                    >
                      <PenTool className="w-4 h-4 mr-2" />
                      {signatureStatus?.has_signature ? 'Re-semnează' : 'Semnează Pontajul'}
                    </Button>
                  )}
                  
                  {signatureStatus?.signature_count >= 2 && (
                    <Badge className="bg-green-600 text-white">
                      <Check className="w-3 h-3 mr-1" />
                      Complet semnat
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        
        {/* Earnings Section - Collapsible at bottom */}
        {attendanceData && (
          <div className="mt-4 border-t pt-4">
            <button
              onClick={() => setShowEarnings(!showEarnings)}
              className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200 hover:border-amber-300 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-amber-600" />
                <span className="font-medium text-amber-800">Sumar câștiguri {monthNames[month - 1]} {year}</span>
              </div>
              {showEarnings ? (
                <ChevronUp className="w-5 h-5 text-amber-600" />
              ) : (
                <ChevronDown className="w-5 h-5 text-amber-600" />
              )}
            </button>
            
            {showEarnings && (() => {
              const totalHours = stats.oreLucrate;
              const totalDays = stats.zileLucrate;
              const hourlyRate = userSalary ? (userSalary / 168) : 24.1;
              const estimatedEarnings = totalHours * hourlyRate;
              const progressPercent = Math.min((totalHours / 168) * 100, 100);
              
              return (
                <div className="mt-3 p-4 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl text-white">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-white/15 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="w-4 h-4 opacity-80" />
                        <span className="text-xs opacity-90">Ore Lucrate</span>
                      </div>
                      <p className="text-2xl font-bold">{totalHours.toFixed(1)}</p>
                      <p className="text-xs opacity-75">{totalDays} zile</p>
                    </div>
                    
                    <div className="bg-white/15 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Wallet className="w-4 h-4 opacity-80" />
                        <span className="text-xs opacity-90">Câștig Estimat</span>
                      </div>
                      <p className="text-2xl font-bold">{Math.round(estimatedEarnings).toLocaleString()}</p>
                      <p className="text-xs opacity-75">RON ({hourlyRate.toFixed(1)}/h)</p>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="opacity-80">Progres normă lunară (168h)</span>
                      <span className="font-medium">{Math.round(progressPercent)}%</span>
                    </div>
                    <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-white rounded-full transition-all duration-500"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
        
        {/* Signature Pad Dialog */}
        <SignaturePad
          open={showSignaturePad}
          onOpenChange={setShowSignaturePad}
          userId={userId}
          userName={userName}
          companyId={companyId}
          month={month}
          year={year}
          onSignatureComplete={handleSignatureComplete}
        />
      </DialogContent>
    </Dialog>
  );
}
